import React, { useState, useEffect } from "react";
import Cross from "@assets/svg/CrossIcon";
import {
  Modal,
  Pressable,
  View,
  StyleSheet,
  TouchableWithoutFeedback,
  FlatList,
  TouchableOpacity,
  Linking,
  Platform,
} from "react-native";
import Text from "@components/CustomText";
import { colors } from "@constants/theme";
import SkeletonLoaderList from "@components/SkeletonLoaderList";
import Phone from "@assets/svg/MyTasks/Call";
import { api } from "@services/api";
import handleCallButtonPress from "@utils/handleCallButtonPress";
import ToastMessage from "@components/Toast";

const CustomerModal = ({ showModal, setshowModal, data }: any) => {
  const [loading, setLoading] = useState(false);
  const [listItems, setListItems] = useState([]);

  useEffect(() => {
    if (showModal && data?.rvp_item_ids) {
      fetchCustomerData();
    }
  }, [showModal, data]);

  const fetchCustomerData = async () => {
    try {
      setLoading(true);
      const response = await api.post(data.model_api, {
        rvp_item_ids: data.rvp_item_ids,
      });
      setListItems(response.data?.cx_list || []);
    } catch (error) {
      console.error("Error fetching customer modal data:", error);
    } finally {
      setLoading(false);
    }
  };
  const onPressCall = (phone) => {
    handleCallButtonPress(phone, "CX_NUMBER_CLICKED", {
      screen: "Return Detail",
    });
  };
  const openMaps = (latitude, longitude) => {
    try {
      Linking.openURL(
        Platform.OS === "ios"
          ? `https://maps.apple.com/maps?daddr=${latitude},${longitude}`
          : `https://maps.google.com/maps?q=loc:${latitude},${longitude}`,
      );
    } catch (error) {
      ToastMessage("error", error);
    }
  };

  const renderItem = (item) => {
    return (
      <View style={styles.customerContainer}>
        <View style={styles.customerRow}>
          <View style={styles.quantityContainer}>
            <View style={styles.quantityBox}>
              <Text style={styles.quantityText}>{item.quantity}</Text>
            </View>
            <Text variant="caption_3" style={styles.quantityLabel}>
              Qty.
            </Text>
          </View>
          <View style={styles.customerInfo}>
            <Text variant="body_text_2" style={styles.userName}>
              {item.name}
            </Text>
            <TouchableOpacity
              onPress={() => openMaps(item.cx_lat, item.cx_lng)}
              activeOpacity={0.7}
            >
              <Text variant="body_text_3" style={styles.location}>
                {item.address}
              </Text>
            </TouchableOpacity>
            <View style={styles.bottomRow}>
              {item.phoneNumbers.map((num, index) =>
                num !== "" ? (
                  <TouchableOpacity
                    key={num}
                    style={styles.phoneNumberContainer}
                    onPress={() => onPressCall(num)}
                    activeOpacity={0.7}
                  >
                    <View key={index} style={styles.phoneNumberCircle}>
                      <Text variant="caption_2" style={styles.phoneNumberText}>
                        {index + 1}
                      </Text>
                    </View>
                    <Phone />
                  </TouchableOpacity>
                ) : null,
              )}
              <View style={styles.orderDetails}>
                <Text variant="caption_3" style={styles.orderIdText}>
                  Order ID: {item.order_id}
                </Text>
                <Text variant="caption_3" style={styles.deliveryDateText}>
                  Delivered Date: {item.deliveredDate}
                </Text>
              </View>
            </View>
          </View>
        </View>
      </View>
    );
  };

  return (
    <Modal
      statusBarTranslucent
      navigationBarTranslucent
      animationType="fade"
      transparent={true}
      visible={!!showModal}
      onRequestClose={() => setshowModal(false)}
    >
      <TouchableWithoutFeedback onPress={() => setshowModal(false)}>
        <View style={styles.modalBackground} />
      </TouchableWithoutFeedback>
      <View style={styles.contentContainer}>
        <View style={styles.modalCross}>
          <Pressable onPress={() => setshowModal(false)}>
            <Cross width={16} height={16} />
          </Pressable>
        </View>
        {loading ? (
          <View style={styles.loaderContainer}>
            <SkeletonLoaderList isLoading={loading} length={5} />
          </View>
        ) : (
          <>
            <View style={styles.headingContainer}>
              <Text variant="title_20">Customer Details</Text>
            </View>
            <View style={styles.container}>
              <FlatList
                data={listItems}
                renderItem={({ item }) => renderItem(item)}
                keyExtractor={(item) => item.order_id}
                showsVerticalScrollIndicator={false}
                ListFooterComponent={() => <View style={styles.footer} />}
              />
            </View>
          </>
        )}
      </View>
    </Modal>
  );
};

export default CustomerModal;

const styles = StyleSheet.create({
  modalBackground: {
    flex: 1,
    backgroundColor: colors.mediumBlacklightBlack,
    justifyContent: "flex-end",
    alignItems: "center",
  },
  contentContainer: {
    backgroundColor: colors.white,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    width: "100%",
    position: "absolute",
    bottom: 0,
    maxHeight: "70%",
  },
  headingContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    padding: 16,
    alignItems: "center",
  },
  modalCross: {
    position: "absolute",
    right: 10,
    top: -35,
    backgroundColor: colors.white,
    borderRadius: 50,
    zIndex: 1,
    padding: 6,
  },
  container: {
    padding: 16,
    paddingTop: 0,
  },
  loaderContainer: {
    padding: 16,
  },
  customerContainer: {
    marginBottom: 16,
    padding: 16,
    backgroundColor: colors.white,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.lighterBlack,
  },
  customerRow: {
    flexDirection: "row",
    alignItems: "flex-start",
  },
  quantityContainer: {
    justifyContent: "center",
    alignItems: "center",
    marginRight: 10,
  },
  quantityBox: {
    backgroundColor: colors.black,
    borderRadius: 4,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  quantityText: {
    color: colors.white,
  },
  quantityLabel: {
    color: colors.mediumBlacklightBlack,
  },
  customerInfo: {
    flex: 1,
    marginLeft: 4,
  },
  userName: {
    marginBottom: 5,
  },
  location: {
    color: "#4274EA",
    textDecorationLine: "underline",
  },
  bottomRow: {
    flexDirection: "row",
    marginTop: 10,
    justifyContent: "space-between",
    alignItems: "center",
  },
  phoneNumberContainer: {
    marginRight: 12,
  },
  phoneNumberCircle: {
    backgroundColor: colors.black,
    borderRadius: 25,
    width: 18,
    height: 18,
    justifyContent: "center",
    alignItems: "center",
    position: "absolute",
    bottom: -4,
    right: -8,
    zIndex: 100,
    borderWidth: 1,
    borderColor: colors.white,
  },
  phoneNumberText: {
    color: colors.white,
    lineHeight: 18,
  },
  orderDetails: {
    alignItems: "flex-end",
    flex: 1,
  },
  orderIdText: {
    color: colors.black06,
    fontWeight: "600",
    fontSize: 11,
  },
  deliveryDateText: {
    color: colors.black06,
    marginTop: 5,
    fontWeight: "600",
    fontSize: 11,
  },
  footer: {
    height: 50,
  },
});
